import { useRef, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/sections/Footer";
import { Connect } from "../components/sections/Connect";
import { ScrollTimeline } from "../components/ScrollTimeline";
import { Reveal } from "../components/Reveal";
import { IMAGES, HOW_JOURNEY, MILESTONES, KAUPAPA_VALUES } from "../lib/content";
import { Check, Leaf } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

// ---- Cinematic banner ----
const Banner = ({ image, alt, overline, title, subtitle, height = "70vh" }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1.15, 1]);
  return (
    <section
      ref={ref}
      data-testid="how-hero"
      className="relative w-full overflow-hidden bg-forest-deep flex items-center justify-center"
      style={{ minHeight: height }}
    >
      <motion.img
        style={{ scale }}
        src={image}
        alt={alt}
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-forest-deep/65" />
      <div className="absolute inset-0 bg-gradient-to-t from-forest-deep via-transparent to-forest-deep/50" />
      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center pt-20">
        <Reveal>
          {overline && <p className="overline text-gold mb-6">{overline}</p>}
          <h1 className="font-serif-display text-cream text-4xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[1.03]">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-8 max-w-2xl mx-auto text-cream/80 font-light text-lg sm:text-xl leading-relaxed">
              {subtitle}
            </p>
          )}
        </Reveal>
      </div>
    </section>
  );
};

// ---- Editorial text section ----
const Prose = ({ overline, heading, children, theme = "light", align = "left" }) => {
  const themes = {
    light: "bg-softwhite text-forest",
    cream: "bg-cream text-forest",
    dark: "bg-ink text-cream",
    forest: "bg-forest text-cream",
    earth: "bg-earth text-cream",
  };
  const overlineColor = theme === "light" || theme === "cream" ? "text-earth" : "text-gold";
  return (
    <section className={`${themes[theme]} py-24 sm:py-32 lg:py-40`}>
      <div className={`max-w-3xl mx-auto px-6 sm:px-8 ${align === "center" ? "text-center" : ""}`}>
        <Reveal>
          {overline && <p className={`overline ${overlineColor} mb-6`}>{overline}</p>}
          {heading && (
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] mb-10">
              {heading}
            </h2>
          )}
        </Reveal>
        <div
          className={`space-y-6 font-light text-lg sm:text-xl leading-relaxed ${
            theme === "light" || theme === "cream" ? "text-ink/70" : "text-cream/80"
          }`}
        >
          {children}
        </div>
      </div>
    </section>
  );
};

const P = ({ children, delay = 0 }) => (
  <Reveal delay={delay} y={30}>
    <p>{children}</p>
  </Reveal>
);

export default function HowItWorks() {
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <div data-testid="how-it-works-page" className="relative bg-softwhite">
      <Navbar />

      {/* Opening */}
      <Banner
        image={IMAGES.heroForest}
        alt="Native bush at dawn"
        overline="Te Kaupapa — Kei te Whakatū · In Its Establishment Phase"
        title="How Ngā Kākano Wāhine Works"
        subtitle="A carefully researched social enterprise, currently being designed. This is an invitation to help bring it to life."
        height="88vh"
      />

      {/* Opening statement */}
      <Prose overline="Tō Mātou Whakapono · Our Belief" heading="Built on one simple belief." theme="dark">
        <Reveal y={30}>
          <p className="font-serif-display text-3xl sm:text-4xl text-cream leading-tight">
            You see a past.
            <br />
            <span className="text-gold italic">We see potential.</span>
          </p>
        </Reveal>
        <P delay={0.05}>
          We believe every wāhine deserves the opportunity to rediscover her
          mana through education, enterprise, leadership and belief.
        </P>
        <P delay={0.1}>
          We are not creating another programme. We are designing an ecosystem —
          and we are seeking the right people to help us build it.
        </P>
      </Prose>

      {/* Why Rongoā */}
      <section className="relative bg-cream text-forest py-24 sm:py-32 lg:py-40">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <div className="relative aspect-[4/5] overflow-hidden">
              <motion.img
                src={IMAGES.fernLight}
                alt="Native flora and rongoā"
                initial={{ scale: 1.12 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.6, ease: EASE }}
                className="h-full w-full object-cover"
              />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="overline text-earth mb-6">Te Rongoā · Why Rongoā?</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl font-light tracking-tight leading-[1.05] mb-8">
              He taonga tuku iho — a treasure, honoured with respect.
            </h2>
            <div className="space-y-6 text-ink/70 font-light text-lg leading-relaxed">
              <p>
                Rongoā is a taonga and an important part of te ao Māori. Our
                intention is not to teach rongoā or replace the knowledge of
                practitioners.
              </p>
              <p>
                Our intention is to respectfully incorporate rongoā into the
                participant journey as a way of reconnecting wāhine with culture,
                whenua, wellbeing and identity — while creating a meaningful
                social enterprise.
              </p>
              <p className="text-forest">
                The enterprise begins with premium rongoā tea blends. These
                products create income. That income helps create opportunity for
                wāhine. Business becomes the vehicle that supports the kaupapa.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* The Journey timeline */}
      <section className="relative bg-softwhite py-24 sm:py-32 lg:py-40">
        <div className="max-w-3xl mx-auto px-6 text-center mb-20">
          <Reveal>
            <p className="overline text-earth mb-6">Te Ara · The Journey</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-forest">
              The pathway we envision.
            </h2>
          </Reveal>
        </div>
        <ScrollTimeline stages={HOW_JOURNEY} />
      </section>

      {/* Why Volunteers */}
      <Prose overline="Ngā Tūao · Why Volunteers?" heading="Learning, immediately applied." theme="forest">
        <P>
          We intend for participants who choose to join Ngā Kākano to volunteer
          alongside their Level 3 business studies. This would allow them to gain
          real business experience while they learn.
        </P>
        <P delay={0.05}>
          Every hour is designed to contribute toward building confidence,
          practical capability and leadership — with learning immediately applied
          inside a real enterprise.
        </P>
      </Prose>

      {/* Business Accelerator */}
      <Prose overline="Te Whakatere Pakihi · Business Accelerator" heading="Developing their own businesses." theme="cream">
        <P>
          Participants who continue into Level 4 may apply for the Ngā Kākano
          Business Accelerator. Our intention is that eligible participants begin
          developing their own businesses while still receiving mentoring and
          support.
        </P>
      </Prose>

      {/* Micro Startup Pilot — milestones */}
      <section className="relative bg-ink text-cream py-24 sm:py-32 lg:py-40">
        <div className="max-w-5xl mx-auto px-6 sm:px-8">
          <Reveal className="max-w-2xl mb-14">
            <p className="overline text-gold mb-6">Te Pia Whakarewa · Micro Startup Pilot</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl font-light tracking-tight leading-[1.05]">
              Support released as milestones are achieved.
            </h2>
            <p className="mt-8 text-cream/70 font-light text-lg leading-relaxed">
              We are designing support to be milestone-based — released in stages
              as each achievement is reached.
            </p>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-cream/10 border border-cream/10">
            {MILESTONES.map((m, i) => (
              <Reveal
                key={m}
                delay={(i % 4) * 0.06}
                testId={`milestone-${i}`}
                className="bg-ink p-6 sm:p-8 flex flex-col gap-4 min-h-[8rem]"
              >
                <Check className="text-gold" strokeWidth={1.2} size={22} />
                <span className="font-serif-display text-xl sm:text-2xl font-light">{m}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Growing the Ecosystem */}
      <Prose overline="Te Whakatipu i te Pūnaha · Growing the Ecosystem" heading="Success creates more success." theme="earth">
        <P>
          Graduates would own their businesses. Ngā Kākano does not seek
          ownership. Instead, participants who receive significant startup support
          may enter into a transparent, fair and time-limited partnership
          agreement.
        </P>
        <P delay={0.05}>
          This may include a modest capped revenue share or agreed contribution
          for a defined period. Those contributions would help create
          opportunities for the next generation of wāhine.
        </P>
        <Reveal delay={0.1} y={30}>
          <p className="font-serif-display text-2xl sm:text-3xl text-gold italic pt-4">
            Opportunity creates more opportunity. The ecosystem becomes
            self-sustaining.
          </p>
        </Reveal>
      </Prose>

      {/* Why This Matters */}
      <section className="relative bg-softwhite text-forest py-24 sm:py-32 lg:py-40">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 text-center">
          <Reveal>
            <p className="overline text-earth mb-10">He Aha Ai · Why This Matters</p>
          </Reveal>
          <div className="space-y-8">
            {[
              ["One", "successful business can change one family."],
              ["One hundred", "businesses can change communities."],
              ["One thousand", "businesses can change generations."],
            ].map(([n, t], i) => (
              <Reveal key={n} delay={i * 0.1} y={30}>
                <p className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-light leading-tight">
                  <span className="text-earth">{n}</span> {t}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Our Kaupapa */}
      <section className="relative bg-forest text-cream py-24 sm:py-32 lg:py-40 overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.07] bg-cover bg-center"
          style={{ backgroundImage: `url(${IMAGES.rootsTexture})` }}
        />
        <div className="relative max-w-5xl mx-auto px-6 sm:px-8">
          <Reveal className="max-w-3xl mb-16">
            <p className="overline text-gold mb-6">Our Kaupapa</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05]">
              Guided by the spirit of Te Tiriti o Waitangi.
            </h2>
            <p className="mt-8 text-cream/75 font-light text-lg leading-relaxed">
              Our aspiration is that every decision, relationship, partnership and
              future direction reflects the values held within a metaphorical
              korowai — protecting the kaupapa before commercial success, and
              people before profit.
            </p>
          </Reveal>

          <div className="grid sm:grid-cols-2 gap-px bg-cream/10 border border-cream/10">
            {KAUPAPA_VALUES.map((v, i) => (
              <Reveal
                key={v.reo}
                delay={(i % 2) * 0.06}
                testId={`kaupapa-${i}`}
                className="bg-forest p-7 sm:p-8 flex items-start gap-4"
              >
                <Leaf className="text-gold mt-1 shrink-0" strokeWidth={1.2} size={20} />
                <div>
                  <h3 className="font-serif-display text-2xl font-light">{v.reo}</h3>
                  <p className="text-cream/60 font-light text-sm mt-1">{v.en}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-16 max-w-3xl" y={30}>
            <p className="text-cream/75 font-light text-lg leading-relaxed">
              This vision is still in its establishment phase. Cultural leadership
              from kaumātua, iwi, hapū, whānau and recognised experts will
              continue to shape and strengthen the organisation as it grows —
              honouring tikanga in both governance and practice.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Partnership */}
      <Prose overline="Te Mahi Tahi · Partnership" heading="This vision cannot be built alone." theme="dark">
        <P>
          This vision cannot be built by one person or one organisation. It
          requires collaboration.
        </P>
        <P delay={0.05}>
          We are seeking iwi, educators, business leaders, rongoā practitioners,
          governance professionals, legal advisors, accountants, funders,
          community organisations, mentors and industry experts who believe in
          creating long-term opportunity for wāhine.
        </P>
        <Reveal delay={0.1} y={30}>
          <p className="text-cream/90">
            Every partner brings unique knowledge. Every contribution strengthens
            the ecosystem.
          </p>
        </Reveal>
      </Prose>

      {/* Working Alongside Others */}
      <Prose overline="Kotahitanga · Working Alongside Others" heading="Together, not in competition." theme="cream">
        <P>
          Ngā Kākano Wāhine has no desire to replace or compete with the
          incredible organisations already serving our communities. There are
          many people doing exceptional mahi across Aotearoa, and we acknowledge
          and respect that work.
        </P>
        <P delay={0.05}>
          Our intention is to complement existing services by creating a
          long-term entrepreneurship and business pathway that can sit alongside
          the support already available.
        </P>
        <P delay={0.1}>
          We believe lasting change happens when organisations work together,
          share knowledge, and build on each other’s strengths.
        </P>
      </Prose>

      {/* A Seat at the Table */}
      <section className="relative bg-ink text-cream py-24 sm:py-32 lg:py-40 overflow-hidden">
        <div className="max-w-3xl mx-auto px-6 sm:px-8">
          <Reveal>
            <p className="overline text-gold mb-6">He Tūru mōu · A Seat at the Table</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] mb-10">
              We are bringing together the people who can shape this vision.
            </h2>
          </Reveal>
          <div className="space-y-6 text-cream/80 font-light text-lg sm:text-xl leading-relaxed">
            <P>Not just through funding. Through wisdom. Experience. Partnership. Constructive challenge.</P>
            <P delay={0.05}>
              If you believe you have knowledge, expertise or resources that could
              help strengthen this kaupapa, we invite you to take a seat at the
              table.
            </P>
            <Reveal delay={0.1} y={30}>
              <p className="font-serif-display text-2xl sm:text-3xl text-gold italic pt-2">
                Together, we can build something that creates opportunity not just
                for today, but for generations to come.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Connect */}
      <Connect />
      <Footer />
    </div>
  );
}
